import { ArrowUpRight } from "lucide-react";
import { useSite } from "@/lib/SiteContext";
import { useT } from "@/lib/i18n";
import { buildWhatsAppUrl } from "@/lib/constants";
import Reveal from "./Reveal";

const IMG = {
  // Real Incubatour photo: ProColombia / Colombia destination booth at international fair
  misiones:
    "https://customer-assets.emergentagent.com/job_curated-missions/artifacts/1kny4lqb_Captura%20de%20pantalla%202026-04-29%20a%20la%28s%29%209.56.47%E2%80%AFa.m..png",
  // Real Incubatour photo: facilitator with team in retreat / immersive session
  transformadoras:
    "https://customer-assets.emergentagent.com/job_curated-missions/artifacts/9r3r0kdq_Captura%20de%20pantalla%202026-04-29%20a%20la%28s%29%209.58.23%E2%80%AFa.m..png",
  // Real Incubatour photo: travelers experiencing premium hosted dining / boutique evening
  curados:
    "https://customer-assets.emergentagent.com/job_curated-missions/artifacts/jmvntpx7_Captura%20de%20pantalla%202026-04-29%20a%20la%28s%29%209.58.04%E2%80%AFa.m..png",
};

const LineCard = ({ data, image, imageAlt, index, code, classification, ctaWaMessage, ctaSource }) => {
  const isReverse = index % 2 === 1;
  return (
    <div
      className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 ${
        isReverse ? "lg:[&>*:first-child]:order-2" : ""
      }`}
      data-testid={`line-card-${index}`}
    >
      {/* Image — diamond clip, sticky on lg+ to balance long content */}
      <div className="lg:col-span-5 relative">
        <div className="lg:sticky lg:top-24">
          <Reveal>
            <div className="relative">
              {/* gradient backplate */}
              <div
                aria-hidden
                className="absolute -top-3 -left-3 w-full h-full opacity-90"
                style={{
                  background: code === "01"
                    ? "linear-gradient(135deg, #FF1F8F 0%, #FCB424 100%)"
                    : code === "02"
                    ? "linear-gradient(135deg, #FCB424 0%, #8AE52E 100%)"
                    : "linear-gradient(135deg, #8AE52E 0%, #2DD4BF 100%)",
                  clipPath: "polygon(22% 0%, 100% 0%, 78% 100%, 0% 100%)",
                }}
              />
              <div
                className="ix-img-zoom relative aspect-[4/5] w-full overflow-hidden"
                style={{ clipPath: "polygon(22% 0%, 100% 0%, 78% 100%, 0% 100%)" }}
              >
                <img src={image} alt={imageAlt || data.title} loading="lazy" className="w-full h-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Content */}
      <Reveal delay={140} className="lg:col-span-7">
        {/* Audience chip — explicit "for whom" so first-time visitor instantly knows */}
        {data.audience && (
          <div
            className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 ix-mono text-[0.66rem] uppercase tracking-[0.18em]"
            style={{
              background: code === "01"
                ? "linear-gradient(135deg, rgba(255,31,143,0.15) 0%, rgba(252,180,36,0.15) 100%)"
                : code === "02"
                ? "linear-gradient(135deg, rgba(252,180,36,0.15) 0%, rgba(138,229,46,0.15) 100%)"
                : "linear-gradient(135deg, rgba(138,229,46,0.15) 0%, rgba(45,212,191,0.15) 100%)",
              border: "1px solid rgba(252, 244, 224, 0.18)",
            }}
            data-testid={`audience-${code}`}
          >
            <span className="ix-grad-text font-semibold">◆</span>
            <span className="text-[var(--ix-cream)]/95">{data.audience}</span>
          </div>
        )}

        {/* File header */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span className="ix-mono text-[0.66rem] uppercase tracking-[0.2em] text-[var(--ix-cream)]/55">
            ▸ Caso · {code}
          </span>
          <span className="w-6 h-px bg-[var(--ix-cream)]/25" />
          <span className="ix-tag px-2.5 py-1 border border-[var(--ix-cream)]/25 text-[var(--ix-cream)]/75">
            {classification}
          </span>
        </div>

        {/* Massive numeral */}
        <div className="flex items-baseline gap-6 mb-2">
          <span className="ix-numeral ix-grad-text leading-none">{code}</span>
        </div>

        <h3 className="ix-display ix-headline-md mt-2 text-[var(--ix-cream)]">
          {data.title}
        </h3>
        <div className="mt-3 ix-mono text-[0.74rem] uppercase tracking-[0.16em] text-[var(--ix-cream)]/60 max-w-md">
          {data.subtitle}
        </div>

        <p className="mt-7 text-[1rem] leading-relaxed text-[var(--ix-cream)]/85 max-w-xl">
          {data.description}
        </p>
        <p className="mt-3 text-[0.95rem] text-[var(--ix-cream)]/60 max-w-xl border-l-2 border-[var(--ix-cream)]/15 pl-3" style={{ fontWeight: 400 }}>
          {data.anti}
        </p>

        {/* Mission types — 3 prominent cards (replaces standalone flagship callout since types[1] already marks academic as flagship) */}
        {data.types && data.types.length > 0 && (
          <div className="mt-10" data-testid={`types-${index}`}>
            <div className="ix-mono text-[0.7rem] uppercase tracking-[0.2em] text-[var(--ix-cream)]/55 flex items-center gap-2 mb-5">
              <span className="ix-grad-text font-semibold">▸</span>
              {data.types_label}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.types.map((tp, i) => {
                const grads = [
                  "linear-gradient(135deg, #FF1F8F 0%, #FCB424 100%)",
                  "linear-gradient(135deg, #FCB424 0%, #8AE52E 100%)",
                  "linear-gradient(135deg, #8AE52E 0%, #2DD4BF 100%)",
                ];
                return (
                  <div
                    key={tp.code}
                    className="relative p-5 border border-[var(--ix-cream)]/15 hover:border-[var(--ix-cream)]/35 transition-colors"
                    style={{ background: tp.flagship ? "rgba(252,244,224,0.04)" : "rgba(17, 21, 43, 0.4)" }}
                    data-testid={`type-${index}-${i}`}
                  >
                    {/* Diamond accent + code */}
                    <div className="flex items-center justify-between mb-3">
                      <span
                        aria-hidden
                        style={{
                          width: 14, height: 18,
                          background: grads[i],
                          clipPath: "polygon(22% 0%, 100% 0%, 78% 100%, 0% 100%)",
                        }}
                      />
                      <span className="ix-mono text-[0.72rem] tracking-[0.14em] text-[var(--ix-cream)]/45">
                        {tp.code}
                      </span>
                    </div>
                    {tp.flagship && (
                      <div className="absolute top-3 left-9 ix-mono text-[0.58rem] uppercase tracking-[0.16em] ix-grad-text font-bold">
                        ★ {data.flagship_label}
                      </div>
                    )}
                    <h4 className="ix-display text-[1.45rem] leading-tight text-[var(--ix-cream)] mt-2" style={{ fontWeight: 700 }}>
                      {tp.name}
                    </h4>
                    <div className="ix-mono text-[0.66rem] uppercase tracking-[0.16em] text-[var(--ix-cream)]/60 mt-1.5">
                      {tp.tag}
                    </div>
                    <p className="mt-4 text-[0.95rem] leading-relaxed text-[var(--ix-cream)]/85">
                      {tp.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Qué ganas */}
        <div className="mt-9 max-w-xl">
          <div className="ix-mono text-[0.66rem] uppercase tracking-[0.2em] text-[var(--ix-cream)]/50 flex items-center gap-2">
            <span className="ix-grad-text font-semibold">▸</span>
            {data.benefits_label}
          </div>
          <ul className="mt-3 space-y-2">
            {data.benefits.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-[0.95rem] text-[var(--ix-cream)]/90">
                <span
                  aria-hidden
                  className="flex-shrink-0 mt-2"
                  style={{
                    width: 8, height: 11,
                    background: "linear-gradient(135deg, #FF1F8F, #8AE52E)",
                    clipPath: "polygon(22% 0%, 100% 0%, 78% 100%, 0% 100%)",
                  }}
                />
                {b}
              </li>
            ))}
          </ul>
        </div>

        {/* Cómo operamos */}
        <div className="mt-7 max-w-xl">
          <div className="ix-mono text-[0.66rem] uppercase tracking-[0.2em] text-[var(--ix-cream)]/50">
            {data.operation_label}
          </div>
          <ul className="mt-3 space-y-2">
            {data.operation.map((o, idx) => (
              <li key={o} className="flex items-start gap-3 text-[0.93rem] text-[var(--ix-cream)]/75 leading-relaxed">
                <span className="ix-mono text-[0.7rem] text-[var(--ix-cream)]/45 mt-1 flex-shrink-0">
                  0{idx + 1}
                </span>
                {o}
              </li>
            ))}
          </ul>
        </div>

        {/* Formats — small chips */}
        <div className="mt-7">
          <div className="flex flex-wrap items-center gap-2">
            <span className="ix-mono text-[0.66rem] uppercase tracking-[0.2em] text-[var(--ix-cream)]/50 mr-1">
              {data.formats_label}:
            </span>
            {data.formats.map((f) => (
              <span
                key={f}
                className="ix-mono px-2.5 py-1 text-[0.7rem] uppercase tracking-[0.1em] border border-[var(--ix-cream)]/18 text-[var(--ix-cream)]/75 rounded-full"
              >
                {f}
              </span>
            ))}
          </div>
        </div>

        {/* Events (only if provided) — fairs and major events operated */}
        {data.events && data.events.length > 0 && (
          <div className="mt-8 max-w-xl">
            <div className="ix-mono text-[0.66rem] uppercase tracking-[0.2em] text-[var(--ix-cream)]/50 mb-3 flex items-center gap-2">
              <span className="ix-grad-text font-semibold">▸</span>
              {data.events_label}
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
              {data.events.map((ev, idx) => (
                <li
                  key={ev}
                  className="flex items-start gap-3 text-[0.92rem] text-[var(--ix-cream)]/85 leading-snug"
                  data-testid={`event-${index}-${idx}`}
                >
                  <span className="ix-mono text-[0.7rem] text-[var(--ix-cream)]/45 mt-1 flex-shrink-0">
                    ◆
                  </span>
                  <span className="ix-display" style={{ fontWeight: 500 }}>{ev}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Results (only if provided) — measurable outcomes from missions */}
        {data.results && data.results.length > 0 && (
          <div
            className="mt-8 max-w-xl border-l-2 pl-5 py-2"
            style={{ borderImage: "linear-gradient(180deg, #FF1F8F 0%, #FCB424 50%, #8AE52E 100%) 1" }}
            data-testid={`results-${index}`}
          >
            <div className="ix-mono text-[0.66rem] uppercase tracking-[0.2em] text-[var(--ix-cream)]/50 mb-3 flex items-center gap-2">
              <span className="ix-grad-text font-semibold">▸</span>
              {data.results_label}
            </div>
            <ul className="space-y-2">
              {data.results.map((r, idx) => (
                <li
                  key={r}
                  className="text-[0.95rem] text-[var(--ix-cream)]/90 leading-relaxed"
                  data-testid={`result-${index}-${idx}`}
                >
                  {r}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Clients (only if provided) */}
        {data.clients && data.clients.length > 0 && (
          <div className="mt-8 max-w-xl border-t border-[var(--ix-cream)]/12 pt-6">
            <div className="ix-mono text-[0.66rem] uppercase tracking-[0.2em] text-[var(--ix-cream)]/50 mb-3">
              {data.clients_label}
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2">
              {data.clients.map((c) => (
                <span key={c} className="text-[0.88rem] text-[var(--ix-cream)]/85 ix-display" style={{ fontWeight: 500 }}>
                  {c}
                </span>
              ))}
            </div>
          </div>
        )}

        <a
          href={buildWhatsAppUrl(ctaWaMessage)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackWaClick(ctaSource, data.title)}
          className="ix-btn ix-btn-grad mt-10"
          data-testid={`line-cta-${index}`}
        >
          {data.cta}
          <ArrowUpRight size={15} strokeWidth={1.8} />
        </a>
      </Reveal>
    </div>
  );
};

export const LinesOfExperience = () => {
  const { locale } = useSite();
  const { t } = useT(locale);

  const waMessages = {
    es: {
      mision: "Hola, quiero diseñar una misión / representación / participación en feria con Incubatour Experiences Lab. ¿Podemos hablar?",
    },
    en: {
      mision: "Hi, I'd like to design a mission / fair representation with Incubatour Experiences Lab. Can we talk?",
    },
  };
  const msg = waMessages[locale] || waMessages.es;

  const lines = [
    {
      key: "misiones",
      waMsg: msg.mision,
      waSource: "line_misiones",
      code: "01",
      classification: locale === "es" ? "Empresariales · Académicas · Tecnológicas" : "Commercial · Academic · Technical",
      img: IMG.misiones,
      imgAlt: locale === "es"
        ? "Delegación liderada por Incubatour Experiences Lab representando a Colombia con bandera en una feria internacional de turismo"
        : "Delegation led by Incubatour Experiences Lab representing Colombia with national flag at an international tourism fair",
      data: t("lineas.misiones"),
    },
  ];

  return (
    <section
      id="lineas"
      className="relative py-28 md:py-36"
      style={{ backgroundColor: "var(--ix-night)", color: "var(--ix-cream)" }}
      data-testid="lines-section"
    >
      <div className="max-w-[1480px] mx-auto px-5 md:px-10 lg:px-14 relative">
        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20 md:mb-28">
            <div className="lg:col-span-4">
              <div className="ix-mono text-[0.7rem] uppercase tracking-[0.18em] flex items-center gap-2">
                <span className="ix-grad-text font-semibold">◆</span>
                {t("lineas.eyebrow")}
              </div>
            </div>
            <div className="lg:col-span-8">
              <h2 className="ix-display ix-headline-lg text-[var(--ix-cream)]">
                {t("lineas.title")}
              </h2>
              <p className="mt-6 text-[1rem] leading-relaxed text-[var(--ix-cream)]/65 max-w-2xl">
                {t("lineas.subtitle")}
              </p>
            </div>
          </div>
        </Reveal>

        <div className="space-y-28 md:space-y-40">
          {lines.map((l, i) => (
            <LineCard
              key={l.key}
              data={l.data}
              image={l.img}
              imageAlt={l.imgAlt}
              index={i}
              code={l.code}
              classification={l.classification}
              ctaWaMessage={l.waMsg}
              ctaSource={l.waSource}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default LinesOfExperience;
